<template>
	<transition name="modal">
		<div
			v-if="isOpen"
			class="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4 font-sans"
			@click.self="closeModal">
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-dropdown w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-primary-100 dark:border-dark-600">
				<!-- ── Header ── -->
				<div
					class="flex items-center justify-between p-6 border-b border-primary-100 dark:border-dark-600">
					<div class="flex items-center gap-3">
						<div
							class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
							<svg
								class="w-5 h-5 text-primary-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M5.121 17.804A13.937 13.937 0 0112 15c2.633 0 5.086.758 7.121 2.064M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
							</svg>
						</div>
						<div>
							<h2
								class="font-display text-[16px] font-bold text-dark-700 dark:text-white">
								Edit Profile
							</h2>
							<p class="text-[12px] text-muted dark:text-muted-light mt-0.5">
								Update your organization information
							</p>
						</div>
					</div>
					<button
						@click="$emit('close')"
						class="w-8 h-8 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center text-muted dark:text-muted-light hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors duration-150">
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2.5"
								d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<!-- ── Avatar ── -->
				<div
					class="flex flex-col items-center py-6 px-6 border-b border-primary-50 dark:border-dark-600">
					<div class="relative mb-3">
						<img
							:src="profile.imageURL || defaultImage"
							class="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-dark-600 shadow-card"
							alt="Profile" />
						<label
							for="upload"
							class="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary-400 flex items-center justify-center cursor-pointer hover:bg-primary-600 shadow-teal transition-colors duration-200">
							<svg
								class="w-3.5 h-3.5 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15.232 5.232l3.536 3.536M9 11l6 6m0 0l3.536-3.536M15 17H5a2 2 0 01-2-2V5a2 2 0 012-2h7" />
							</svg>
						</label>
						<input
							id="upload"
							type="file"
							class="hidden"
							@change="handleImageUpload" />
					</div>
					<p class="text-[11px] text-muted dark:text-muted-light">
						Recommended: Square image, at least 200×200px
					</p>
				</div>

				<!-- ── Form ── -->
				<form @submit.prevent="saveProfile" class="p-6 space-y-5">
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">
						Organization Information
					</p>

					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<!-- Org Name -->
						<div>
							<label class="modal-label">Organization Name *</label>
							<input
								v-model="profile.name"
								type="text"
								required
								class="modal-input" />
						</div>

						<!-- Handle -->
						<div>
							<label class="modal-label">Handle</label>
							<div v-if="profile.handle">
								<input
									v-model="profile.handle"
									disabled
									type="text"
									class="modal-input opacity-60 cursor-not-allowed" />
							</div>
							<div v-else>
								<button
									@click="generateHandle"
									:disabled="isLoading"
									type="button"
									class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold bg-primary-400 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
									<svg
										v-if="isLoading"
										class="animate-spin w-4 h-4"
										fill="none"
										viewBox="0 0 24 24">
										<circle
											class="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											stroke-width="4" />
										<path
											class="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
									</svg>
									{{ isLoading ? "Generating..." : "Generate Handle" }}
								</button>
							</div>
						</div>
					</div>

					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<!-- Phone -->
						<div>
							<label class="modal-label">Phone Number *</label>
							<input
								v-model="profile.phone"
								type="tel"
								required
								class="modal-input"
								:class="{ 'border-red-400': errors.phone }" />
							<p v-if="errors.phone" class="text-[11px] text-red-400 mt-1">
								{{ errors.phone }}
							</p>
						</div>
						<!-- Email -->
						<div>
							<label class="modal-label">Email Address *</label>
							<input
								v-model="profile.email"
								type="email"
								required
								class="modal-input"
								:class="{ 'border-red-400': errors.email }" />
							<p v-if="errors.email" class="text-[11px] text-red-400 mt-1">
								{{ errors.email }}
							</p>
						</div>
					</div>

					<!-- Description -->
					<div>
						<label class="modal-label">Organization Description</label>
						<textarea
							v-model="profile.description"
							rows="3"
							maxlength="500"
							required
							class="modal-input resize-none" />
						<div class="flex justify-end mt-1">
							<span
								class="text-[11px]"
								:class="
									profile.description.length > 450
										? 'text-red-400'
										: 'text-muted dark:text-muted-light'
								">
								{{ profile.description.length }}/500
							</span>
						</div>
					</div>

					<!-- Save -->
					<button
						type="submit"
						:disabled="isLoading"
						class="w-full font-display font-bold text-[14px] py-3.5 rounded-xl bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200">
						<span
							v-if="isSubmitLoading"
							class="flex items-center justify-center gap-2">
							<svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4" />
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
							</svg>
							Saving...
						</span>
						<span v-else class="flex items-center justify-center gap-2">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2.5"
									d="M5 13l4 4L19 7" />
							</svg>
							Save Changes
						</span>
					</button>
				</form>
			</div>
		</div>
	</transition>
</template>

<script>
import { apiDataService } from "@/services/apiDataService";
import { ref, reactive, watch } from "vue";
import { showToast } from "@/constants/toast";

export default {
	name: "AccountSettingsModal",
	props: {
		isOpen: { type: Boolean, required: true },
		user: { type: Object, default: () => ({}) },
	},
	setup(props, { emit }) {
		const defaultImage =
			"https://ci3.googleusercontent.com/meips/ADKq_NalT97SLaewwQ55QtdyBHYcv-EFN-V-3yMo1pXp5phcPU2khSKZVQz3yO9VEzvSBbn5jsT7t9ylW1VR=s0-d-e1-ft#http://138.68.125.153:8020/trekko.png";
		const isLoading = ref(false);
		const isSubmitLoading = ref(false);
		const errors = reactive({ email: "", phone: "" });

		const profile = reactive({
			imageURL: "",
			name: "",
			email: "",
			phone: "",
			description: "",
			handle: "",
			logo: "",
			organization_id: "",
		});

		const mapApiResponseToProfile = (apiData) => ({
			organization_id: apiData.ID || apiData.organization_id,
			name: apiData.Name || apiData.name || apiData.organization_name,
			email: apiData.Email || apiData.email || apiData.organization_email,
			phone: apiData.Phone || apiData.phone,
			description:
				apiData.Description ||
				apiData.description ||
				apiData.organization_description,
			imageURL:
				apiData.Logo ||
				apiData.logo ||
				apiData.organization_logo ||
				defaultImage,
			handle: apiData.handle || apiData.Handle,
			logo: apiData.logo || "",
		});

		watch(
			() => props.user,
			(newUser) => {
				if (newUser) Object.assign(profile, mapApiResponseToProfile(newUser));
			},
			{ immediate: true, deep: true },
		);
		watch(
			() => props.isOpen,
			(open) => {
				if (open && props.user)
					Object.assign(profile, mapApiResponseToProfile(props.user));
			},
		);

		const handleKeydown = (e) => {
			if (e.key === "Escape") closeModal();
		};
		watch(
			() => props.isOpen,
			(open) => {
				if (open) {
					window.addEventListener("keydown", handleKeydown);
					document.body.style.overflow = "hidden";
				} else {
					window.removeEventListener("keydown", handleKeydown);
					document.body.style.overflow = "";
				}
			},
			{ immediate: true },
		);

		const handleImageUpload = (e) => {
			const file = e.target.files[0];
			if (!file) return;
			if (!file.type.startsWith("image/")) {
				showToast("error", "Please upload an image file.");
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				showToast("error", "File size exceeds 5MB.");
				return;
			}
			profile.logo = file;
			profile.imageURL = URL.createObjectURL(file);
		};

		const validateForm = () => {
			let isValid = true;
			errors.email = "";
			errors.phone = "";
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const phoneRegex = /^\+?\d{9,15}$/;
			if (!emailRegex.test(profile.email)) {
				errors.email = "Invalid email format.";
				isValid = false;
			}
			if (!phoneRegex.test(profile.phone)) {
				errors.phone = "Invalid phone number format.";
				isValid = false;
			}
			if (!profile.name.trim() || !profile.description.trim()) {
				showToast("error", "Please fill all required fields.");
				isValid = false;
			}
			return isValid;
		};

		const closeModal = () => {
			errors.email = "";
			errors.phone = "";
			emit("close");
		};

		const saveProfile = async () => {
			if (!validateForm()) return;
			const formData = new FormData();
			formData.append("name", profile.name);
			formData.append("description", profile.description);
			formData.append("email", profile.email);
			formData.append("phone", profile.phone);
			if (profile.logo && typeof profile.logo !== "string")
				formData.append("logo", profile.logo);
			try {
				isSubmitLoading.value = true;
				const response = await apiDataService.editOrganization(
					profile.organization_id,
					formData,
				);
				const updatedProfile = mapApiResponseToProfile(response.data);
				Object.assign(profile, updatedProfile);
				showToast("success", "Profile updated successfully!");
				emit("profile-updated", {
					organization_id: updatedProfile.organization_id,
					organization_name: updatedProfile.name,
					organization_email: updatedProfile.email,
					phone: updatedProfile.phone,
					organization_description: updatedProfile.description,
					organization_logo: updatedProfile.imageURL,
					handle: updatedProfile.handle,
					ID: response.data.ID,
					CreatedAt: response.data.CreatedAt,
					UpdatedAt: response.data.UpdatedAt,
				});
				closeModal();
			} catch (error) {
				console.error("Update error:", error);
				showToast("error", "Oops! Something went wrong.");
			} finally {
				isSubmitLoading.value = false;
			}
		};

		const generateHandle = async () => {
			if (profile.name && profile.organization_id) {
				try {
					isLoading.value = true;
					const response = await apiDataService.generateOrganisationDomain(
						profile.organization_id,
						profile.name,
					);
					const newHandle = response.data.handle || response.data.Handle;
					profile.handle = newHandle;
					showToast("success", `Handle generated: ${newHandle}`);
					emit("profile-updated", {
						...props.user,
						handle: newHandle,
						organization_name: profile.name,
					});
				} catch (error) {
					console.error("Handle generation error:", error);
					showToast("error", "Failed to generate handle.");
				} finally {
					isLoading.value = false;
				}
			}
		};

		return {
			profile,
			closeModal,
			saveProfile,
			defaultImage,
			errors,
			handleImageUpload,
			generateHandle,
			isLoading,
			isSubmitLoading,
		};
	},
};
</script>

<style scoped>
.modal-label {
	@apply block text-[12px] font-bold uppercase tracking-widest mb-2
         text-muted dark:text-muted-light;
}
.modal-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium
         bg-primary-50/60 dark:bg-dark-600
         border border-primary-100 dark:border-dark-500
         text-dark-700 dark:text-white
         placeholder-muted dark:placeholder-muted-light
         outline-none transition-all duration-200
         focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20
         dark:focus:border-primary-400;
}
.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}
</style>
